import type { MyDataAccess, FieldListDto } from '@bk/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiDownload, errorMessage } from '../../lib/api';
import {
  archiveFilters,
  archiveKind,
  choiceField,
  fieldKind,
  fieldLabel,
  type ColumnFilter,
  type Sort,
} from '../../lib/field-values';
import { ColumnControl, SortHeading, sortAria } from './ListColumn';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Button, Card, Empty, ErrorText, Field, PageTitle, SectionLoader, Select } from '../../ui';

type Row = {
  id: string;
  attributi: Record<string, unknown>;
  versione: number;
  modificabili?: string[];
  puo_eliminare?: boolean;
  ambito?: string;
  sedi?: string[];
};
interface CardData {
  entita: Row;
  relazioni: Array<Record<string, unknown>>;
  documenti: Array<{ id?: string; documento_id?: string; titolo?: string }>;
}

/** Area operativa con sole proiezioni già autorizzate dal server. */
export function MyDataPage() {
  const q = useTenantQuery<MyDataAccess>(['my-data-access'], '/my-data-access');
  const [chosen, setChosen] = useState('');
  const type = q.data?.types.find((x) => x.key === chosen) ?? q.data?.types[0];
  return (
    <>
      <PageTitle
        title="I dati del tuo lavoro"
        subtitle="Vedi e modifichi i dati consentiti dai tuoi ruoli nelle sedi in cui operi."
      />
      {q.isLoading && <SectionLoader />}
      <ErrorText>{q.error ? errorMessage(q.error) : ''}</ErrorText>
      {q.data && !q.data.types.length && (
        <Empty>I tuoi ruoli non hanno ancora permessi sui dati. Rivolgiti al responsabile.</Empty>
      )}
      {type && (
        <>
          <Select label="Dati" value={type.key} onChange={(e) => setChosen(e.target.value)}>
            {q.data!.types.map((x) => (
              <option key={x.key} value={x.key}>
                {x.label}
              </option>
            ))}
          </Select>
          <DataList key={type.key} type={type} lists={q.data?.lists ?? []} />
        </>
      )}
    </>
  );
}

function DataList({
  type,
  lists: listData,
}: {
  type: MyDataAccess['types'][number];
  lists: FieldListDto[];
}) {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const lists = new Map(listData.map((l) => [l.chiave, l]));
  const fields = type.attributes.map((f) => ({ ...f, nome: f.name }));
  const [filters, setFilters] = useState<Record<string, ColumnFilter>>({});
  const [sort, setSort] = useState<Sort>({ key: fields[0]?.nome ?? '', direction: 'asc' });
  const updateSort = (s: Sort) => {
    setSort(s);
    setOffset(0);
  };
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const search = useQuery({
    queryKey: ['tenant', tenant.id, 'my-data', type.key, query, offset, filters, sort],
    queryFn: () =>
      api<{ righe: Row[]; totale: number; opzioni?: Record<string, string[]> }>(
        t('/archive/search'),
        {
          body: {
            tipo: type.key,
            testo: query || null,
            limite: 50,
            offset,
            elenchi: fields
              .filter((f) => choiceField(f) && fieldKind(f) !== 'si_no')
              .map((f) => f.nome),
            filtri: archiveFilters(fields, filters),
            ordina_per: sort.key || null,
            direzione: sort.direction,
            tipo_ordine: archiveKind(fields.find((f) => f.nome === sort.key)),
          },
        },
      ),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['tenant', tenant.id, 'my-data'] });
    setSelected(null);
  };
  return (
    <Card className="mt-4">
      <form
        className="mb-4 flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (text === query && offset === 0) void search.refetch();
          else {
            setQuery(text);
            setOffset(0);
          }
        }}
      >
        <Field
          label="Cerca nei campi visibili"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <Button type="submit" disabled={search.isFetching}>
          Cerca
        </Button>
        {type.can_create && (
          <Button type="button" variant="ghost" onClick={() => setSelected('new')}>
            Nuova voce
          </Button>
        )}
      </form>
      <ErrorText>{search.error ? errorMessage(search.error) : ''}</ErrorText>
      {search.isLoading && <SectionLoader />}
      {search.data && (
        <>
          <p className="mb-2 text-xs text-ink/60">{search.data.totale} voci nel tuo ambito.</p>
          <div className="overflow-x-auto">
            <table className="bk-list">
              <thead>
                <tr>
                  {type.attributes.map((f) => (
                    <th key={f.name} aria-sort={sortAria(sort, f.name)}>
                      <SortHeading label={f.label} field={f.name} sort={sort} onSort={updateSort} />
                    </th>
                  ))}
                  <th />
                </tr>
                <tr>
                  {fields.map((f) => (
                    <th key={f.nome}>
                      <ColumnControl
                        attr={f}
                        label={f.label}
                        value={filters[f.nome] ?? {}}
                        options={
                          choiceField(f) && fieldKind(f) !== 'si_no'
                            ? (search.data?.opzioni?.[f.nome] ?? []).map((v) => ({
                                value: v,
                                label: fieldLabel(f, v, lists),
                              }))
                            : undefined
                        }
                        onChange={(v) => {
                          setFilters({ ...filters, [f.nome]: v });
                          setOffset(0);
                        }}
                      />
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {search.data.righe.map((row) => (
                  <tr key={row.id}>
                    {type.attributes.map((f) => (
                      <td key={f.name} className="border-b border-black/5 px-2 py-2">
                        {fieldLabel(f, row.attributi[f.name], lists) || '—'}
                      </td>
                    ))}
                    <td>
                      <Button variant="ghost" onClick={() => setSelected(row.id)}>
                        Apri
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Button
              variant="ghost"
              disabled={!offset || search.isFetching}
              onClick={() => setOffset(Math.max(0, offset - 50))}
            >
              Indietro
            </Button>
            <Button
              variant="ghost"
              disabled={offset + 50 >= search.data.totale || search.isFetching}
              onClick={() => setOffset(offset + 50)}
            >
              Avanti
            </Button>
          </div>
        </>
      )}
      {selected && (
        <DataEditor
          key={selected}
          id={selected}
          type={type}
          onSaved={refresh}
          onClose={() => setSelected(null)}
        />
      )}
    </Card>
  );
}

const renderValue = (value: unknown) => value === undefined || value === null ? '—' : typeof value === 'object' ? JSON.stringify(value) : String(value);
function DataEditor({ id, type, onSaved, onClose }: { id: string; type: MyDataAccess['types'][number]; onSaved: () => void; onClose: () => void }) {
  const { t } = useTenant();
  const creating = id === 'new';
  const q = useTenantQuery<CardData>(['my-data-card', type.key, id], `/archive/entities/${encodeURIComponent(type.key)}/${encodeURIComponent(id)}`, { enabled: !creating });
  const [changes, setChanges] = useState<Record<string, string>>({});
  const [site, setSite] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [newId] = useState(() => crypto.randomUUID());
  const scope = type as typeof type & { sites?: Array<{ id: string; name: string }>; company?: boolean };
  const writable = (f: typeof type.attributes[number]) => q.data?.entita.modificabili ? q.data.entita.modificabili.includes(f.name) : f.writable;
  const save = useMutation({ mutationFn: () => api(t(`/archive/entities/${encodeURIComponent(type.key)}/${creating ? newId : encodeURIComponent(id)}`), { method: 'PATCH', body: { attributes: Object.fromEntries(Object.entries(changes).map(([k, v]) => [k, v.trim() === '' ? null : v])), expected_version: creating ? null : q.data!.entita.versione, ...(creating ? { site_id: site || null } : {}) } }), onSuccess: onSaved });
  const remove = useMutation({ mutationFn: () => api(t(`/archive/entities/${encodeURIComponent(type.key)}/${encodeURIComponent(id)}`), { method: 'DELETE', body: { expected_version: q.data!.entita.versione } }), onSuccess: onSaved });
  return <section className="mt-5 space-y-3 rounded-lg bg-black/[0.03] p-4">
    <h2 className="font-semibold">{creating ? 'Nuova voce' : 'Scheda della voce'}</h2>
    {q.isLoading && !creating ? <SectionLoader /> : <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      {type.attributes.map((f) => <Field key={f.name} label={f.label} disabled={!writable(f)} value={changes[f.name] ?? (q.data?.entita.attributi[f.name] === undefined || q.data?.entita.attributi[f.name] === null ? '' : renderValue(q.data.entita.attributi[f.name]))} onChange={(e) => setChanges((old) => ({ ...old, [f.name]: e.target.value }))} />)}
      {creating && <Select label="Ambito" value={site} onChange={(e) => setSite(e.target.value)}>{scope.company && <option value="">Azienda</option>}{!scope.company && <option value="">Scegli una sede</option>}{scope.sites?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select>}
      <ErrorText>{save.error || q.error ? errorMessage(save.error ?? q.error) : ''}</ErrorText>
      <div className="flex gap-2"><Button type="submit" disabled={save.isPending || !Object.keys(changes).length || (!creating && !q.data) || (creating && !scope.company && !site)}>Salva modifiche</Button><Button type="button" variant="ghost" onClick={onClose}>Chiudi</Button></div>
    </form>}
    {q.data?.documenti.map((d) => <Button key={d.id ?? d.documento_id} variant="ghost" onClick={() => void apiDownload(t(`/archive/documents/${d.id ?? d.documento_id}/file`), d.titolo ?? 'documento')}>{d.titolo ?? 'Documento'}</Button>)}
    {q.data && q.data.relazioni.length > 0 && <div className="space-y-1 text-sm text-ink/65"><p className="font-medium">Collegamenti consentiti</p>{q.data.relazioni.map((r, i) => <p key={i}>{[r.significato, r.entity_label ?? r.entity_type].filter(Boolean).map(String).join(' · ')}{r.attributi && typeof r.attributi === 'object' ? `: ${Object.entries(r.attributi).map(([name, value]) => `${name}: ${renderValue(value)}`).join(', ')}` : ''}</p>)}</div>}
    {q.data?.entita.puo_eliminare && <div className="border-t border-black/10 pt-3">{confirmRemove ? <><p className="mb-2 text-sm">Confermi la rimozione di questa voce dal lavoro corrente?</p><div className="flex gap-2"><Button disabled={remove.isPending} onClick={() => remove.mutate()}>Conferma rimozione</Button><Button variant="ghost" onClick={() => setConfirmRemove(false)}>Mantieni la voce</Button></div></> : <Button variant="ghost" onClick={() => setConfirmRemove(true)}>Rimuovi voce</Button>}<ErrorText>{remove.error ? errorMessage(remove.error) : ''}</ErrorText></div>}
  </section>;
}
